import { take, filter, finalize, switchMap, Observable, catchError, throwError, BehaviorSubject } from 'rxjs';

import { inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandlerFn, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';

import { ALLOW_REFRESH, REFRESH_TOKEN_CALLER, SESSION_EXPIRED_HANDLER } from '../tokens';

let isRefreshingToken = false;
const tokenSubject = new BehaviorSubject<string | null>(null);

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const callRefreshToken = inject(REFRESH_TOKEN_CALLER);
  const sessionExpiredHandler = inject(SESSION_EXPIRED_HANDLER, { optional: true });

  const addToken = (reqWithToken: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
    return reqWithToken.clone({
      headers: reqWithToken.headers.set('Authorization', 'Bearer ' + token),
    });
  };

  const handleSessionExpired = (originalError?: HttpErrorResponse) => {
    if (sessionExpiredHandler) {
      sessionExpiredHandler();
    }
    return throwError(() => originalError ?? new HttpErrorResponse({ status: 401 }));
  };

  const handle401Error = (failedReq: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (isRefreshingToken) {
      return tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next(addToken(failedReq, token))),
      );
    }

    isRefreshingToken = true;
    tokenSubject.next(null);

    return callRefreshToken().pipe(
      switchMap((token) => {
        if (!token) {
          return handleSessionExpired();
        }

        tokenSubject.next(token);
        return next(addToken(failedReq, token));
      }),
      catchError((err: unknown) => {
        const httpErr = err instanceof HttpErrorResponse ? err : undefined;
        return handleSessionExpired(httpErr);
      }),
      finalize(() => {
        isRefreshingToken = false;
      }),
    );
  };

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && req.context.get(ALLOW_REFRESH)) {
        return handle401Error(req, next);
      }

      return throwError(() => error);
    }),
  );
};
